import { useEffect, useRef, useState } from "react";
import { authAxios as ax } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useKBSuggestions
 *
 * Real-time AI-assist hook. Watches a list of translation messages (each
 * `{ original, translated }`) and POSTs the recent transcript to
 * `/api/kb/suggested` whenever the stream changes. Debounced + de-duplicated
 * to avoid hammering the backend.
 *
 * @param {Array} messages   - liveTranslation.messages from Softphone
 * @param {boolean} enabled  - only fire while the call is active
 * @param {number} debounceMs- delay before firing the request (default 1500ms)
 */
export function useKBSuggestions(messages, enabled, debounceMs = 1500) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastQuery, setLastQuery] = useState("");
  const timerRef = useRef(null);
  const inflightRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setSuggestions([]);
      clearTimeout(timerRef.current);
      if (inflightRef.current) inflightRef.current.cancel?.("call ended");
      return;
    }
    if (!messages || messages.length === 0) return;

    // Concatenate the last few transcript fragments (original + translated)
    // so we catch keywords regardless of which side spoke them.
    const recent = messages.slice(-4);
    const transcript = recent
      .flatMap((m) => [m.original, m.translated])
      .filter(Boolean)
      .join(" ")
      .slice(0, 600); // cap payload

    if (!transcript || transcript === lastQuery) return;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      inflightRef.current = { cancel: () => controller.abort() };
      setLoading(true);
      try {
        const res = await ax.post(
          `${API}/kb/suggested`,
          { transcript, top_n: 3 },
          { signal: controller.signal }
        );
        setSuggestions(res.data?.suggestions || []);
        setLastQuery(transcript);
      } catch (err) {
        if (err.name !== "CanceledError" && err.name !== "AbortError") {
          // Silent failure — copilot is best-effort
        }
      } finally {
        setLoading(false);
        inflightRef.current = null;
      }
    }, debounceMs);

    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, enabled]);

  return { suggestions, loading };
}
